import { useEffect, useState } from "react";

import { QuestionnaireItem, ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { NativeSelect } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import get from "lodash/get";

import { sortValueSetOptionsByUrl } from "@/intake-forms/utils/value-set-sorting";
import { getValueSetOptions } from "@/utils/medplum";

interface QuestionnaireNativeSelectProps<T> {
  label?: string;
  name: string;
  required: boolean;
  form: UseFormReturnType<T>;
  group: QuestionnaireItem;
  placeholder?: string;
  disabled?: boolean;
  emptyOptionLabel?: string;
  "data-testid"?: string;
}

export default function QuestionnaireNativeSelect<T>({
  label,
  name,
  required,
  form,
  group,
  disabled,
  placeholder = "Select",
  emptyOptionLabel = "None",
  "data-testid": dataTestId,
}: QuestionnaireNativeSelectProps<T>) {
  const [valueSetOptions, setValueSetOptions] = useState<ValueSetExpansionContains[]>([]);
  const medplum = useMedplum();
  const answerValueSet = group?.answerValueSet;
  const currentValue = get(form.values, name) as ValueSetExpansionContains;

  useEffect(() => {
    const getValueSet = async () => {
      if (!answerValueSet) return;

      const valueSetElements = await getValueSetOptions(medplum, answerValueSet);
      if (valueSetElements?.length) {
        setValueSetOptions(sortValueSetOptionsByUrl(answerValueSet, valueSetElements));
      }
    };

    getValueSet();
  }, [answerValueSet, medplum, name, form]);

  return (
    <NativeSelect
      name={name}
      label={label}
      required={required}
      disabled={disabled}
      data-testid={dataTestId}
      value={currentValue ? JSON.stringify(currentValue) : ""}
      data={[
        { value: "", label: currentValue ? emptyOptionLabel : placeholder },
        ...valueSetOptions.map((option) => ({
          value: JSON.stringify(option),
          label: option.display || option.code || "",
        })),
      ]}
      onChange={(event) => {
        const value = event.currentTarget.value;
        if (value) {
          form.setFieldValue(name, JSON.parse(value));
        } else {
          // @ts-expect-error Form value type is complex, but we know empty object is valid here
          form.setFieldValue(name, "");
        }
      }}
    />
  );
}
